
import { Vue, Options } from "vue-class-component";
import {
  ellipsisString,
  formatDateWithoutTime,
  formatTimeCustom,
  isMigrateFromElexys
} from "@/app/infrastructures/misc/Utils";
import formatPriceRP from "@/app/infrastructures/misc/common-library/v2/FormatPriceRP";
import { AccountController } from "@/app/ui/controllers/AccountController";
import formatByCountry, { resolveCountry } from "@/app/infrastructures/misc/common-library/FormatByCountry";
import { PodDexController } from "@/app/ui/controllers/PodDexController";
import EmptyState from "@/app/ui/components/empty-list/index.vue";
import { Pagination } from "@/domain/entities/Pagination";

@Options({
  components: {
    EmptyState
  },
  computed: {
    getTitle() {
      return this.$route.meta.name;
    }
  }
})
export default class StationTransitInDest extends Vue {
  mounted() {
    this.refs = this.$refs;
    PodDexController.setType("CODREJ");
  }
  unmounted() {
    PodDexController.onReset();
  }

  refs: any = "";

  updateNotificationModel = true;

  fetchPodDexList() {
    PodDexController.fetchPodDexList();
  }

  get isFirstRequest() {
    return PodDexController.isFirstRequest;
  }

  // loading
  get isLoading() {
    return PodDexController.isLoading;
  }

  // filter date
  get startDate() {
    return PodDexController.filter.startDate;
  }

  get endDate() {
    return PodDexController.filter.endDate;
  }

  setDateRange(value: Array<any>) {
    PodDexController.setStartDate(value[0]);
    PodDexController.setEndDate(value[1]);
    value[0] && value[1] && this.fetchPodDexList();
  }

  // filter search
  get searchValue() {
    return PodDexController.filter.search;
  }

  onSearch(value: string) {
    PodDexController.setSearch(value);
    PodDexController.setPagination(new Pagination(1, 10));
    this.fetchPodDexList();
  }

  clearSearch() {
    PodDexController.setSearch("");
    this.fetchPodDexList();
  }

  get accountCurrency(): "IDR" | "MYR" {
    return (
      (AccountController.accountData?.accountCurrency as "IDR" | "MYR") ?? "IDR"
    );
  }

  // Table
  get columns() {
    const currency = this.accountCurrency;
    const columns = [
      {
        name: this.$t("podDex.columns.no"),
        styleHead: "w-24 text-left whitespace-no-wrap",
        render: (item: any, index: number) => {
          return `<div class="overflow-ellipsis text-left">${index + 1}</div>`;
        }
      },
      {
        name: this.$t("podDex.columns.noStt"),
        styleHead: "w-50 text-left whitespace-no-wrap",
        render: (item: any) => {
          return `<div class="overflow-ellipsis text-left">${item.sttNo}</div>`;
        }
      },
      {
        name: this.$t("podDex.columns.noReferensi"),
        styleHead: "w-50 text-left whitespace-no-wrap",
        render: (item: any) => {
          return `<div class="overflow-ellipsis text-left">${item.sttNoRefExternal ||
            "-"}</div>`;
        }
      },
      {
        name: this.$t("podDex.columns.totalKoli"),
        styleHead: "w-28 text-left whitespace-no-wrap",
        render: (item: any) => {
          return `<div class="overflow-ellipsis text-left">${formatByCountry(
            item.totalPiece,
            { type: "quantity" }
          )}</div>`;
        }
      },
      {
        name: this.$t("podDex.columns.beratDikenakanBiaya"),
        styleHead: "w-50 text-left whitespace-no-wrap",
        render: (item: any) => {
          return `<div class="overflow-ellipsis text-left">${formatByCountry(
            item.chargeableWeight,
            { type: "weight", suffix: " Kg" }
          )}</div>`;
        }
      },
      {
        name: this.$t("podDex.columns.produk"),
        styleHead: "w-48 text-left whitespace-no-wrap",
        render: (item: any) => {
          return `<div class="text-black-lp-300 flex">
                  <div class="rounded px-2 py-0 bg-gray-lp-400">
                      ${item.productName}
                  </div>
                </div>`;
        }
      },
      {
        name: this.$t("podDex.columns.statusTerakhir"),
        styleHead: "w-48 text-left whitespace-no-wrap",
        render: (item: any) => {
          return `<div class="text-black-lp-300 flex">
                  <div class="rounded px-2 py-0 bg-gray-lp-400">
                    ${item.lastStatus}
                  </div>
                </div>`;
        }
      },
      {
        name: this.$t("podDex.columns.komoditas"),
        styleHead: "w-48 text-left whitespace-no-wrap",
        render: (item: any) => {
          return `<div class="overflow-ellipsis text-left">${item.commodityName}</div>`;
        }
      },
      {
        name: this.$t("podDex.columns.biayaCod"),
        styleHead: "w-50 text-left whitespace-no-wrap",
        render: (item: any) => {
          return `<div class="overflow-ellipsis text-left">${this.formatMoney(
            item.codAmount
          )}</div>`;
        }
      },
      {
        name: this.$t("podDex.columns.tanggalDel"),
        styleHead: "w-50 text-left",
        render: (item: any) => {
          return `<div class="text-black-lp-300">${formatDateWithoutTime(
            item.deliveryDate
          )}</div>
            <div class="capitalize text-gray-lp-500">${formatTimeCustom({
              date: item.deliveryDate,
              isSecond: false
            })}</div>`;
        }
      },
      {
        name: this.$t("podDex.columns.namaKurir"),
        styleHead: "w-48 text-left whitespace-no-wrap",
        render: (item: any) => {
          return `<div class="overflow-ellipsis text-left">${item.courierName}</div>`;
        }
      },
      {
        name: this.$t("podDex.columns.noTelpKurir"),
        styleHead: "w-40 text-left whitespace-no-wrap",
        render: (item: any) => {
          return `<div class="overflow-ellipsis text-left">${item.courierPhoneNumber}</div>`;
        }
      },
      {
        name: this.$t("podDex.columns.dikirimOleh"),
        styleHead: "w-50 text-left",
        vtooltip: (item: any) =>
          item.deliveredBy?.length > 34 ? item.deliveredBy : "",
        render: (item: any) => {
          return `<div class="text-black-lp-300 uppercase">${ellipsisString(
            item.deliveredBy,
            34
          )}</div>
            <div class="capitalize text-gray-lp-500">${
              item.deliveredByType
            }</div>`;
        }
      },
      {
        name: this.$t("podDex.columns.pengirimanKe"),
        styleHead: "w-48 text-left whitespace-no-wrap",
        render: (item: any) => {
          return `<div class="overflow-ellipsis text-left">${formatByCountry(
            item.sttDeliveryAttempt,
            { type: "quantity" }
          )}</div>`;
        }
      }
    ];
    if (!isMigrateFromElexys()) {
      columns.splice(1, 1);
    }
    return columns;
  }

  get pagination() {
    return PodDexController.podDexList.pagination;
  }

  get podDexList() {
    return PodDexController.podDexList.data;
  }

  // handle error
  get isError(): boolean {
    return PodDexController.isError;
  }
  get errorCause() {
    return PodDexController.errorCause;
  }

  formatMoney(value: number): string {
    const country = resolveCountry();
    const prefix = country === "MY" ? "RM" : "Rp";
    return `${prefix}${formatByCountry(value, { type: "money" })}`;
  }
}
